/** First path segments that are InField routes, not a CDF org (unprefixed / legacy URLs). */
export const APP_ROUTE_SEGMENTS = new Set([
  '_static',
  'explore',
  'profile',
  'checklists',
  'overview',
  'activity',
  'templates',
  'schedules',
  'observations',
  'observation',
  'asset',
  'notification',
  'application-configuration',
  'checklistItem',
  'checklist-item',
]);

/** Prefixed `/{org}/{project}/...`, or a flag that the path has no prefix. */
export type ParsedAppPath =
  | { isLegacyPath: true }
  | { isLegacyPath: false; org: string; project: string };

const APP_ROUTE_SEGMENTS_LOWER = new Set(
  [...APP_ROUTE_SEGMENTS].map((segment) => segment.toLowerCase())
);

/** Hostname labels that mark the app. Labels before the marker are the org. */
const ORG_HOST_LABELS = new Set(['infield', 'cognite-infield']);

export type OrgHost = {
  org: string;
  suffix: string;
};

/**
 * `acme.infield.cognite.com` and `acme.infield.staging.cognite.com` are org hosts.
 * `foo.cognite-infield.cogniteapp.com` is an org host. `cognite-infield.cogniteapp.com`
 * and `infield.cognite.com` are not, because nothing precedes the marker.
 */
export const readOrgHost = (hostname: string): OrgHost | undefined => {
  const labels = hostname.trim().toLowerCase().split('.').filter(Boolean);
  const markerIndex = labels.findIndex((label) => ORG_HOST_LABELS.has(label));
  if (markerIndex <= 0) {
    return undefined;
  }

  return {
    org: labels.slice(0, markerIndex).join('.'),
    suffix: labels.slice(markerIndex).join('.'),
  };
};

const collapseDuplicateSlashes = (pathname: string): string =>
  pathname.replace(/\/+/g, '/');

/**
 * Canonical pathname: strips query and hash, ensures a leading `/`, collapses
 * duplicate slashes, and strips trailing slashes (`/` unchanged).
 */
export const normalizePathname = (pathname: string): string => {
  const pathOnly = pathname.split(/[?#]/, 1)[0] ?? '';
  const collapsed = collapseDuplicateSlashes(
    pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`
  );
  if (collapsed === '/') {
    return '/';
  }

  const withoutTrailingSlash = collapsed.replace(/\/+$/, '');
  return withoutTrailingSlash.length > 0 ? withoutTrailingSlash : '/';
};

const isAppRouteSegment = (segment: string): boolean =>
  APP_ROUTE_SEGMENTS_LOWER.has(segment.toLowerCase());

/**
 * Reads org and project from the URL.
 * On `{org}.infield.cognite.com`, org is the hostname label and project is the
 * first path segment. Elsewhere, org and project are `/{org}/{project}/...`.
 * Unprefixed app routes are `{ isLegacyPath: true }`.
 * Omit `hostname` to keep the path-prefix behavior.
 */
export const parseAppPathFromPathname = (
  pathname: string,
  hostname?: string
): ParsedAppPath => {
  const segments = normalizePathname(pathname).split('/').filter(Boolean);
  const orgHost = hostname ? readOrgHost(hostname) : undefined;

  if (segments.length === 0 || isAppRouteSegment(segments[0])) {
    return { isLegacyPath: true };
  }

  if (orgHost) {
    return {
      org: orgHost.org,
      project: segments[0],
      isLegacyPath: false,
    };
  }

  if (segments.length < 2) {
    return { isLegacyPath: true };
  }

  const [org, project] = segments;

  return {
    org,
    project,
    isLegacyPath: false,
  };
};

const routePathAfterPrefix = (
  pathname: string,
  parsed: ParsedAppPath,
  hostname?: string
): string => {
  if (parsed.isLegacyPath) {
    return pathname;
  }

  const orgHost = hostname ? readOrgHost(hostname) : undefined;
  const prefix = orgHost
    ? `/${parsed.project}`
    : `/${parsed.org}/${parsed.project}`;
  return pathname.slice(prefix.length) || '/';
};

/**
 * Builds the app pathname for `org` and `project`.
 * On an org host the path is `/{project}/...`. Elsewhere it is `/{org}/{project}/...`.
 * Replaces an existing prefix when present.
 */
export const buildAppPath = (
  org: string,
  project: string,
  pathname: string,
  hostname?: string
): string => {
  const normalizedOrg = org.trim();
  const normalizedProject = project.trim();
  if (!normalizedOrg) {
    throw new Error('Organization is required');
  }
  if (!normalizedProject) {
    throw new Error('Project name is required');
  }

  const normalizedPathname = normalizePathname(pathname);
  const parsed = parseAppPathFromPathname(normalizedPathname, hostname);
  const routePath = routePathAfterPrefix(normalizedPathname, parsed, hostname);
  const orgHost = hostname ? readOrgHost(hostname) : undefined;
  const prefix = orgHost
    ? `/${normalizedProject}`
    : `/${normalizedOrg}/${normalizedProject}`;

  if (routePath === '/') {
    return prefix;
  }

  return `${prefix}${routePath}`;
};

/**
 * Origin to use when `org` is not the org already in `hostname`.
 * Undefined when the host is not an org subdomain or the org already matches.
 */
export const getOrgSwitchOrigin = (
  org: string,
  hostname: string
): string | undefined => {
  const orgHost = readOrgHost(hostname);
  const normalizedOrg = org.trim().toLowerCase();
  if (!orgHost || !normalizedOrg || normalizedOrg === orgHost.org) {
    return undefined;
  }

  return `https://${normalizedOrg}.${orgHost.suffix}`;
};

/**
 * React Router basename (`/{org}/{project}`, or `/{project}` on an org host)
 * when the pathname is prefixed; `undefined` for legacy / unprefixed paths.
 */
export const getRouterBasenameFromPathname = (
  pathname: string,
  hostname?: string
): string | undefined => {
  const parsed = parseAppPathFromPathname(pathname, hostname);
  if (parsed.isLegacyPath) {
    return undefined;
  }

  const orgHost = hostname ? readOrgHost(hostname) : undefined;
  if (orgHost) {
    return `/${parsed.project}`;
  }

  return `/${parsed.org}/${parsed.project}`;
};
